import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import {
  useMySubscriptions,
  useStayMonthly,
} from "../../../api/hooks/useStrategySubscriptions";
import type { ApiError } from "../../../api/client";
import { useFailure } from "../../../context/useFailure";
import { useToast } from "../../../context/useToast";
import { routePaths } from "../../../routes/route-paths";
import { StrategyPriceText } from "../components/StrategyPrice";
import { formatDateShort } from "../../../lib/dates";
import { StrategySideLabel } from "../components/StrategySideLabel";

// CStepper's exposed imperative API. Mirrors the End and Resume wizards so the flows stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

/** Undoes a waiting upgrade to yearly. Deliberately a copy of the Resume wizard's shape. */
export function SubscriptionStayMonthlyWizardCoreui() {
  const navigate = useNavigate();
  const { strategyId = "" } = useParams<{ strategyId: string }>();

  const { data: subscriptions = [], isLoading, error } = useMySubscriptions();
  const stayMonthlyRemote = useStayMonthly();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const subscription = subscriptions.find(
    (s) => s.strategy.id === strategyId && s.pendingSwitch !== null,
  );

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <CAlert color="danger">
          Unable to load your subscription. Please try again.
        </CAlert>
      </div>
    );
  }

  if (!subscription || !subscription.pendingSwitch) {
    return (
      <div>
        <CAlert color="warning">
          There's no upgrade waiting on this strategy.{" "}
          <Link to={routePaths.myStrategies}>Back to my strategies</Link>.
        </CAlert>
      </div>
    );
  }

  const switchDate = subscription.pendingSwitch.switchDate;
  const detailPath = `/subscriptions/${strategyId}`;

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      await stayMonthlyRemote.mutateAsync(strategyId);
      showSuccess(`${subscription.strategy.publicName} stays monthly.`);
      navigate(detailPath);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to cancel the upgrade. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const monthlyPrice = (
    <StrategyPriceText
      free={subscription.priceAtSubscriptionUsd === 0}
      amountUsd={subscription.priceAtSubscriptionUsd}
      cycle={subscription.billingCycle}
    />
  );

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to cancel the upgrade below. Review the details, then
              continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Strategy
              </dt>
              <dd className="col-bo-9 fw-semibold">
                {subscription.strategy.publicName}
                <div className="small fw-normal mt-1">
                  <StrategySideLabel direction={subscription.strategy.direction} />
                </div>
              </dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Billing now
              </dt>
              <dd className="col-bo-9">{monthlyPrice}</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">
                Upgrading to
              </dt>
              <dd className="col-bo-9">
                Yearly, from {formatDateShort(switchDate)}
              </dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="info">
              Your upgrade to yearly on {formatDateShort(switchDate)} is confirmed.
              Staying monthly cancels it.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                The upgrade is cancelled, so no yearly payment is taken on{" "}
                {formatDateShort(switchDate)}.
              </li>
              <li>
                <strong>{subscription.strategy.publicName}</strong> stays on your
                monthly bill ({monthlyPrice}), renewing on{" "}
                {formatDateShort(switchDate)}.
              </li>
              <li>
                Your access to the strategy doesn't change.
              </li>
              <li>
                You can upgrade again from its page, up to a day before your next
                bill.
              </li>
            </ul>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </CButton>
              <CButton
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting && <CSpinner size="sm" className="me-2" />}
                Stay monthly
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Stay monthly</h2>
      <p className="text-body-secondary">
        Keep <strong>{subscription.strategy.publicName}</strong> on your monthly
        bill.
      </p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
